/**
 * The one place the frontend knows the SHAPE of an RFQ id.
 *
 * The backend stores `RFQ-003-A7K2`: the organization's own sequence, then the
 * organization's own four-character code, which is what makes the id unique
 * across every organization. That suffix is plumbing — a person is only ever
 * shown `RFQ-003`, including in the browser's address bar.
 *
 * So: API payloads carry the stored id and we keep it as-is for every request
 * we send; `displayRfqId` is what goes on screen, and the route builders in
 * `wizardRoutes.ts` run every URL through it. Nothing else may strip a suffix
 * with a regex of its own — one rule, one place.
 *
 * The backend resolves both forms permanently (`recordAccessService`), which is
 * what makes a bare id in the address bar a working link rather than a 404.
 */

/**
 * The org-code alphabet, mirroring user-management's `utils/orgCode.ts` and
 * buyer-service's `utils/rfqIdentity.js`: no O/0, no I/1/L, because people
 * read these codes out loud.
 */
const ORG_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

/**
 * Anchored on the WHOLE id, so it splits `RFQ-003-A7K2` and leaves a legacy
 * bare `RFQ-003` — or `RFQ-2345`, whose digits happen to be code-shaped —
 * exactly as it found them.
 */
const SUFFIXED_ID = new RegExp(`^(RFQ-\\d+)-[${ORG_CODE_ALPHABET}]{4}$`);

/**
 * What a person may see: the bare sequence.
 *
 * @param rfqId the stored id, either form
 * @returns the display form; anything that is not a suffixed id comes back untouched
 */
export const displayRfqId = (rfqId: string | undefined | null): string => {
  if (typeof rfqId !== 'string') return '';
  const match = SUFFIXED_ID.exec(rfqId.trim());
  return match ? match[1] : rfqId;
};

/**
 * The id as it belongs in a URL — the display form, encoded. Every route
 * builder uses this, so the address bar never shows the org code.
 *
 * @param rfqId the stored id, either form
 */
export const rfqIdForUrl = (rfqId: string): string => encodeURIComponent(displayRfqId(rfqId));

/**
 * Do these two ids name the same RFQ? The comparison every "is the loaded RFQ
 * the one the URL asks for" check needs, because the URL carries the display
 * form while an API payload carries the stored one — a raw `===` between the
 * two would never match and the wizard would render nothing.
 *
 * @param a an RFQ id in either form
 * @param b an RFQ id in either form
 */
export const sameRfqId = (a: string | undefined | null, b: string | undefined | null): boolean =>
  Boolean(a) && Boolean(b) && displayRfqId(a) === displayRfqId(b);
